import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material"
import { z } from "zod"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { useAddModel } from "@/hooks/useAddModel"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import ClearIcon from "@mui/icons-material/Clear"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import BillImagePreview from "./BillImagePreview"
import { ExpenseSchema } from "./expenseSchema"

type ExpenseFormValue = z.infer<typeof ExpenseSchema>

interface AddExpenseModalProps {
  open?: boolean
  onClose?: () => void
  defaultValues?: Partial<ExpenseFormValue>
  getNewRecord?: (response: ExpenseFormValue) => void
}

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({ open, onClose }) => {
  const [billImage, setBillImage] = useState<File | null>(null)
  const [removeExistingBill, setRemoveExistingBill] = useState(false)
  const {
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    register,
    handleSubmit,
    handleReset,
    control,
    Controller,
    defaultOnClose,
    api,
    queryClient,
    reset,
    setValue,
  } = useAddModel<ExpenseFormValue>(
    "expense",
    ExpenseSchema,
    "expense",
    onClose
  )

  useEffect(() => {
    if (!selectedRow) {
      setValue("date", dayjs().format("YYYY-MM-DD"))
      setValue("type", "daily")
    }
  }, [selectedRow, setValue])

  const submitExpense = async (values: ExpenseFormValue) => {
    const formData = new FormData()
    formData.append("date", values.date)
    formData.append("amount", values.amount)
    formData.append("title", values.title)
    formData.append("type", values.type)
    if (billImage) formData.append("bill_image", billImage)
    if (removeExistingBill) formData.append("remove_bill_image", "1")
    if (selectedRow) formData.append("_method", "PUT")

    try {
      await api(selectedRow ? `expense/${selectedRow.id}` : "expense", {
        method: "POST",
        body: formData,
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["expense"] }),
        queryClient.invalidateQueries({ queryKey: ["total-daily-expenses"] }),
      ])
      toast.success(
        `Expense ${selectedRow ? "updated" : "created"} successfully`
      )
      reset()
      setBillImage(null)
      setRemoveExistingBill(false)
      onClose ? onClose() : defaultOnClose()
    } catch {
      // The shared form API displays validation and request errors.
    }
  }

  const resetExpense = () => {
    if (selectedRow) {
      handleReset()
    } else {
      reset({
        date: dayjs().format("YYYY-MM-DD"),
        amount: "",
        title: "",
        type: "daily",
      })
    }
    setBillImage(null)
    setRemoveExistingBill(false)
  }

  const selectBillImage = (file?: File) => {
    if (!file) return
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      toast.error("Choose a JPG, PNG, or WebP image under 5 MB")
      return
    }
    setBillImage(file)
    setRemoveExistingBill(false)
  }

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          width: { xs: "95vw", sm: "90vw", md: 500 },
          margin: { xs: "16px auto", md: "48px auto" },
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          padding: { xs: 1, sm: 2 },
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
            flexWrap: "wrap",
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
              color: "black",
            }}
          >
            {selectedRow ? "Update Expense" : "Add New Expense"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit((values) =>
            submitExpense(values as ExpenseFormValue)
          )}
          noValidate
        >
          <Box sx={{ paddingY: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="date"
                control={control}
                defaultValue={dayjs().format("YYYY-MM-DD")}
                render={({ field, fieldState }) => (
                  <DatePicker
                    label="Date"
                    value={field.value ? dayjs(field.value) : dayjs()}
                    onChange={(newValue) => {
                      field.onChange(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                      )
                    }}
                    sx={{
                      width: "100%",
                      "& fieldset": {
                        border: "1px solid #D5D9E2",
                        borderRadius: "7px",
                      },
                    }}
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>

          <TextField
            fullWidth
            type="number"
            label="Amount"
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ marginBottom: 2 }}
            defaultValue={selectedRow?.amount ?? ""}
            error={!!errors.amount}
            helperText={errors.amount?.message?.toString() || ""}
            {...register("amount")}
          />

          <TextField
            fullWidth
            label="Title"
            sx={{ marginBottom: 2 }}
            defaultValue={selectedRow?.title || ""}
            error={!!errors.title}
            helperText={errors.title?.message?.toString() || ""}
            {...register("title")}
          />

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Expense type</FormLabel>
            <Controller
              name="type"
              control={control}
              defaultValue="daily"
              render={({ field }) => (
                <RadioGroup row {...field} value={field.value || "daily"}>
                  <FormControlLabel
                    value="daily"
                    control={<Radio />}
                    label="Daily"
                  />
                  <FormControlLabel
                    value="overall"
                    control={<Radio />}
                    label="Overall"
                  />
                </RadioGroup>
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Only today’s Daily expenses appear in the dashboard total.
            </Typography>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Button component="label" variant="outlined" size="small">
              {billImage ? "Change bill image" : "Add bill image"}
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => {
                  selectBillImage(event.target.files?.[0])
                  event.target.value = ""
                }}
              />
            </Button>
            {billImage && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {billImage.name}
                <Button size="small" onClick={() => setBillImage(null)}>
                  Remove
                </Button>
              </Typography>
            )}
            {selectedRow?.has_bill_image &&
              !billImage &&
              !removeExistingBill && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <BillImagePreview expenseId={selectedRow.id} />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setRemoveExistingBill(true)}
                  >
                    Remove current bill
                  </Button>
                </Box>
              )}
            {removeExistingBill && (
              <Button size="small" onClick={() => setRemoveExistingBill(false)}>
                Undo bill removal
              </Button>
            )}
          </Box>

          {/* Buttons */}
          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={resetExpense}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddExpenseModal
